import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Buildings,
  Lightning,
  Drop,
  Flame,
  Package,
  CaretUpDown,
  Check,
} from '@phosphor-icons/react';
import { useAssetCategories, useUpdateAsset } from 'hooks/useAssets';
import type { UpdateAssetDto } from 'shared/types/asset';
import { useBuildingsAndUnits } from 'hooks/useBuildingsAndUnits';
import MapperNodeCard, { type MapperAsset, type TreeNode, type TableField } from './MapperNodeCard';
import ParentPickerDialog from './ParentPickerDialog';

const TYPE_ICONS: Record<string, any> = {
  'Electriciteit': Lightning,
  'Elektriciteit': Lightning,
  'Gas': Flame,
  'Water': Drop,
};

const VoorzieningenMapper: React.FC = () => {
  const { session } = useSession();
  const { categories } = useAssetCategories();
  const { buildings } = useBuildingsAndUnits();
  const { updateAsset, isUpdating } = useUpdateAsset();

  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [assets, setAssets] = useState<MapperAsset[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Parent picker state
  const [parentPickerAssetId, setParentPickerAssetId] = useState<string | null>(null);

  // Get types for selected category
  const selectedCategory = categories.find(c => c.id === parseInt(selectedCategoryId));
  const availableTypes = selectedCategory?.assetTypes?.filter(t => t.active) || [];

  // Collect fields with showInTable=true (category-level + type-level)
  const tableFields = useMemo((): TableField[] => {
    if (!selectedCategory) return [];
    const fields: TableField[] = [];
    const seen = new Set<string>();

    // Category-level shared fields
    for (const f of selectedCategory.fieldDefinitions || []) {
      if (f.showInTable && f.active !== false && !seen.has(f.fieldName)) {
        fields.push({ fieldName: f.fieldName, displayName: f.displayName });
        seen.add(f.fieldName);
      }
    }

    // Type-level fields
    for (const t of selectedCategory.assetTypes || []) {
      for (const f of t.fieldDefinitions || []) {
        if (f.showInTable && f.active !== false && !seen.has(f.fieldName)) {
          fields.push({ fieldName: f.fieldName, displayName: f.displayName });
          seen.add(f.fieldName);
        }
      }
    }

    return fields;
  }, [selectedCategory]);

  // Fetch hierarchy data
  const fetchHierarchy = useCallback(async () => {
    if (!session || !selectedBuildingId || !selectedCategoryId) return;
    setIsLoading(true);
    try {
      let url = `${API_URL}/assets/hierarchy?buildingId=${selectedBuildingId}&categoryId=${selectedCategoryId}`;
      if (selectedTypeId) url += `&typeId=${selectedTypeId}`;
      const resp = await fetchWithAuth(url, { method: 'GET' }, session);
      if (resp.ok) setAssets(await resp.json());
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [session, selectedBuildingId, selectedCategoryId, selectedTypeId]);

  useEffect(() => {
    if (selectedBuildingId && selectedCategoryId) fetchHierarchy();
    else setAssets([]);
  }, [fetchHierarchy, selectedBuildingId, selectedCategoryId]);

  // Build tree from flat list
  const tree = useMemo((): TreeNode[] => {
    const nodeMap = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    for (const asset of assets) {
      nodeMap.set(asset.id, { asset, children: [], depth: 0 });
    }

    for (const asset of assets) {
      const node = nodeMap.get(asset.id)!;
      if (asset.parentAssetId && nodeMap.has(asset.parentAssetId)) {
        nodeMap.get(asset.parentAssetId)!.children.push(node);
      } else {
        roots.push(node);
      }
    }

    const setDepth = (node: TreeNode, d: number) => {
      node.depth = d;
      node.children.forEach(c => setDepth(c, d + 1));
    };
    roots.forEach(r => setDepth(r, 0));

    return roots;
  }, [assets]);

  // Group roots by type
  const groupedByType = useMemo(() => {
    const groups = new Map<string, TreeNode[]>();
    for (const root of tree) {
      const key = root.asset.typeName;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(root);
    }
    return groups;
  }, [tree]);

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  // Parent picker handlers
  const parentPickerAsset = parentPickerAssetId
    ? assets.find(a => a.id === parentPickerAssetId) || null
    : null;

  const parentCandidates = useMemo(() => {
    if (!parentPickerAssetId) return [];
    // All assets in same category except self (and descendants to prevent cycles)
    const descendants = new Set<string>();
    const collectDescendants = (id: string) => {
      descendants.add(id);
      assets.filter(a => a.parentAssetId === id).forEach(a => collectDescendants(a.id));
    };
    collectDescendants(parentPickerAssetId);

    return assets.filter(a => !descendants.has(a.id));
  }, [parentPickerAssetId, assets]);

  const handleSetParent = useCallback(async (parentId: string | null) => {
    if (!parentPickerAssetId) return;
    try {
      await updateAsset(parentPickerAssetId, { parentAssetId: parentId || null } as UpdateAssetDto);
      toast.success(parentId ? 'Ouder gekoppeld' : 'Ouder ontkoppeld');
      setParentPickerAssetId(null);
      fetchHierarchy();
    } catch (err: any) {
      toast.error(err?.message || 'Fout bij het bijwerken');
    }
  }, [parentPickerAssetId, updateAsset, fetchHierarchy]);

  const handleRemoveParent = useCallback(async (assetId: string) => {
    try {
      await updateAsset(assetId, { parentAssetId: null } as UpdateAssetDto);
      toast.success('Ouder ontkoppeld');
      fetchHierarchy();
    } catch (err: any) {
      toast.error(err?.message || 'Fout bij het bijwerken');
    }
  }, [updateAsset, fetchHierarchy]);

  const [buildingOpen, setBuildingOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-end gap-4 flex-wrap">
        {/* Building searchable combobox */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Gebouw</span>
          <Popover open={buildingOpen} onOpenChange={setBuildingOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-[280px] justify-between h-9 text-sm font-normal">
                {selectedBuildingId
                  ? `${selectedBuildingId} — ${buildings.find(b => b.id === selectedBuildingId)?.company?.name || ''}`
                  : 'Selecteer gebouw...'}
                <CaretUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[350px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Zoek gebouw..." />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>Geen gebouwen gevonden</CommandEmpty>
                  <CommandGroup>
                    {buildings.map(b => (
                      <CommandItem
                        key={b.id}
                        value={`${b.id} ${b.company?.name || ''} ${(b as any).googlePlaceLongAddress || ''}`}
                        onSelect={() => { setSelectedBuildingId(b.id); setBuildingOpen(false); }}
                      >
                        <div className="flex flex-col flex-1">
                          <span className="text-sm font-medium">{b.id}</span>
                          <span className="text-xs text-muted-foreground">{b.company?.name} — {(b as any).googlePlaceLongAddress || ''}</span>
                        </div>
                        {selectedBuildingId === b.id && <Check className="h-4 w-4 text-primary ml-2" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Category searchable combobox */}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Categorie</span>
          <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" className="w-[200px] justify-between h-9 text-sm font-normal">
                {selectedCategoryId ? categories.find(c => c.id === parseInt(selectedCategoryId))?.name : 'Selecteer categorie...'}
                <CaretUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Zoek categorie..." />
                <CommandList className="max-h-[250px]">
                  <CommandEmpty>Geen categorieën gevonden</CommandEmpty>
                  <CommandGroup>
                    {categories.filter(c => c.active).map(c => (
                      <CommandItem
                        key={c.id}
                        value={c.name}
                        onSelect={() => { setSelectedCategoryId(String(c.id)); setSelectedTypeId(''); setCategoryOpen(false); }}
                      >
                        {c.name}
                        {selectedCategoryId === String(c.id) && <Check className="h-4 w-4 text-primary ml-auto" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Type filter */}
        {availableTypes.length > 1 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">Type (optioneel)</span>
            <select
              value={selectedTypeId}
              onChange={(e) => setSelectedTypeId(e.target.value)}
              className="h-9 w-[200px] rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Alle types</option>
              {availableTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      {!selectedBuildingId || !selectedCategoryId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Buildings className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              Selecteer een gebouw en categorie om de voorzieningen te bekijken
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : assets.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              Geen voorzieningen gevonden voor {selectedBuilding?.id || 'dit gebouw'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Building header */}
          <div className="flex items-center gap-2">
            <Buildings className="h-5 w-5 text-muted-foreground" />
            <span className="font-semibold">{selectedBuildingId}</span>
            {(selectedBuilding as any)?.googlePlaceLongAddress && (
              <span className="text-sm text-muted-foreground">{(selectedBuilding as any).googlePlaceLongAddress}</span>
            )}
            <Badge variant="secondary" className="ml-auto">{assets.length} voorzieningen</Badge>
          </div>

          {/* Grouped by type */}
          {Array.from(groupedByType.entries()).map(([typeName, nodes]) => (
            <div key={typeName}>
              <div className="flex items-center gap-2 mb-3">
                {(() => { const Icon = TYPE_ICONS[typeName] || Package; return <Icon className="h-4 w-4" weight="duotone" />; })()}
                <span className="text-sm font-semibold">{typeName}</span>
                <span className="text-xs text-muted-foreground">({nodes.length} bovenliggend)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {nodes.map(node => (
                  <div key={node.asset.id}>
                    <MapperNodeCard
                      node={node}
                      tableFields={tableFields}
                      onSetParent={setParentPickerAssetId}
                      onRemoveParent={handleRemoveParent}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Parent picker dialog */}
      <ParentPickerDialog
        open={!!parentPickerAssetId}
        onClose={() => setParentPickerAssetId(null)}
        onSelect={handleSetParent}
        asset={parentPickerAsset}
        candidates={parentCandidates}
        tableFields={tableFields}
        isUpdating={isUpdating}
      />
    </div>
  );
};

export default VoorzieningenMapper;
